import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Building2, Rocket, Globe2, TrendingUp, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAgency } from "@/lib/roles";
import { Button } from "@/components/ui/button";

const ICONS = { Starter: Zap, Growth: TrendingUp, Pro: Rocket, "Agency Starter": Zap, "Agency Pro": TrendingUp, "Agency Enterprise": Rocket };

export default function Upgrade() {
  const { user } = useAuth();
  const [audience, setAudience] = useState(isAgency(user?.role) ? "agency" : "company");
  const [plans, setPlans] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => { setPlans(r.data); setMarkets(r.data.cross_country_markets || []); });
  }, []);

  const checkout = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const list = plans ? plans[audience] : [];
  const currentPlan = user?.plan;
  const trial = user?.trial;

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 pb-28 lg:pb-10">
        {/* hero */}
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/25 rounded-full px-4 py-1.5 mb-4"><Sparkles size={12} /> Your hiring engine</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-50 leading-tight">Get qualified candidates,<br />not just a dashboard</h1>
          <p className="mt-4 text-slate-400">WorkOnIT finds people around your vacancies, matches them and drives applications straight to you. Start with a <span className="text-teal-300 font-medium">{plans?.trial_days || 30}-day free trial</span> — no card required to explore.</p>
          {trial?.on_trial && (
            <div data-testid="upgrade-trial-note" className="mt-4 inline-block text-sm text-slate-300 bg-slate-900/60 border border-white/10 rounded-full px-4 py-2">
              {trial.active ? <>You're on the free trial · <span className="text-teal-300 font-mono">{trial.days_left} days left</span></> : <>Your free trial has ended — pick a plan to continue</>}
            </div>
          )}
          <div className="mt-5">
            <a href="/workonit-plans.html" download data-testid="download-plans-html"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-200 border border-white/15 bg-white/5 hover:bg-white/10 rounded-lg px-4 py-2 transition-colors">
              <Download size={15} /> Download plans (HTML)
            </a>
          </div>
        </div>

        {/* audience toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex liquid-glass rounded-xl p-1" data-testid="audience-toggle">
            {[["company", "Companies", Building2], ["agency", "Agencies", Sparkles]].map(([v, label, Icon]) => (
              <button key={v} onClick={() => setAudience(v)} data-testid={`audience-${v}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${audience === v ? "bg-teal-500/15 text-teal-300" : "text-slate-400 hover:text-slate-200"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {list.map((p) => {
            const Icon = ICONS[p.name] || Zap;
            const current = currentPlan === p.id;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl p-6 flex flex-col transition-transform hover:-translate-y-1 ${p.recommended ? "liquid-glass border-2 border-teal-500/50 shadow-[0_0_40px_-12px_rgba(45,212,191,0.4)]" : "liquid-glass"}`}>
                {p.recommended && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 px-3 py-1 rounded-full">Most popular</span>}
                <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-300 mb-4"><Icon size={20} /></div>
                <h3 className="font-display font-bold text-xl text-slate-50">{p.name}</h3>
                <p className="text-slate-400 text-sm mt-1">{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-4xl text-slate-50">€{p.price_eur.toLocaleString()}</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>
                {p.cross_country ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2.5 py-1 w-fit"><Globe2 size={12} /> Cross-country EU search</div>
                ) : (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400 bg-slate-800/60 border border-white/10 rounded-full px-2.5 py-1 w-fit">Local candidates</div>
                )}
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300"><Check size={16} className="text-teal-400 shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <Button onClick={() => checkout(p)} disabled={busy === p.id || current} data-testid={`plan-cta-${p.id}`}
                  className={`w-full mt-6 font-semibold ${p.recommended ? "bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 hover:opacity-90" : "bg-white/5 border border-white/15 text-slate-100 hover:bg-white/10"}`}>
                  {current ? "Current plan" : busy === p.id ? "Redirecting…" : "Choose " + p.name}
                </Button>
              </div>
            );
          })}
        </div>

        {/* performance-fee note */}
        <div className="mt-8 liquid-glass rounded-2xl p-5 flex items-start gap-3" data-testid="performance-fee-note">
          <TrendingUp size={20} className="text-cyan-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-300">Prefer to pay for outcomes? Ask us about our <span className="text-cyan-300 font-medium">performance model</span> — a lower base plus a small fee per qualified application (e.g. <span className="font-mono">€499/mo + €30 per qualified applicant</span>), so your spend scales with the value you receive.</p>
        </div>

        {/* cross-country markets */}
        <div className="mt-6">
          <div className="flex items-center gap-2 text-slate-300 text-sm mb-3"><Globe2 size={16} className="text-teal-400" /> Growth, Pro & Agency plans unlock talent search across Europe's high-supply markets:</div>
          <div className="flex flex-wrap gap-2">
            {markets.map((m) => <span key={m} className="text-xs px-2.5 py-1 rounded-full bg-slate-800/70 text-slate-300 border border-white/10">{m}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}
